import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { reportService } from "../api/services/reportService";
import {
  ScreenOnOffReportData,
  ScreenOffHistoryData,
  ScreenOffHistoryFilters,
  ScreenTimelineData,
  ScreenTimelineFilters,
  CreativeReportSummary,
  CreativeScreenPlayInfo,
  ActiveScreenReportData,
  ActiveScreenInfo,
  OffScreenInfo,
  ScreenWithoutScheduleInfo,
  ReportListPage,
  OffScreenColumnFilter,
  NoScheduleColumnFilter,
} from "../schemas/reportSchema";
import { DEFAULT_PAGE_SIZE } from "../constants";

/**
 * How often the On/Off report re-reads the server, and the staleness budget
 * shared by every query on that page.
 *
 * These describe live screen state, not a static report: the app-wide default
 * (staleTime 5m, no polling) left the summary and charts frozen at whatever
 * they were when the page mounted, while each tab's table fetched fresh the
 * moment it was opened — so the header disagreed with the table below it until
 * a full page reload. Every query on the page now refreshes on the same
 * cadence, which is also the device heartbeat interval.
 */
export const REPORT_REFRESH_INTERVAL_MS = 60_000;
const REPORT_STALE_TIME_MS = 0;

// Summary counts and chart series for the On/Off report. Deliberately takes no
// filters — the graphics always describe the whole tenant, while the tables
// below them filter per column.
export const useScreenOnOffReport = () => {
  const {
    data: reportData,
    isLoading,
    error,
    refetch,
  } = useQuery<ScreenOnOffReportData, Error>({
    queryKey: ["screenOnOffReport"],
    queryFn: () => reportService.getScreenOnOffReport(),
    staleTime: REPORT_STALE_TIME_MS,
    refetchInterval: REPORT_REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  return {
    reportData,
    isLoading,
    error,
    refetch,
  };
};

/**
 * Shared plumbing for the paginated report tables. Rows load a page at a time
 * as the user scrolls; because the filters live in `queryKey`, changing one
 * re-keys the query and the list restarts from the top on its own.
 */
function useInfiniteReportList<TItem>(
  queryKey: unknown[],
  fetchPage: (
    limit: number,
    offset: number,
  ) => Promise<ReportListPage<TItem>>,
  options: { enabled?: boolean; pageSize?: number } = {}
) {
  const { enabled = true, pageSize = DEFAULT_PAGE_SIZE } = options;

  const query = useInfiniteQuery<ReportListPage<TItem>, Error>({
    queryKey,
    // These endpoints are limit/offset based, so the page param is the offset.
    queryFn: ({ pageParam }) => fetchPage(pageSize, pageParam as number),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      // Stop on an empty page so a totalCount/items mismatch can't loop forever.
      if (lastPage.items.length === 0) return undefined;
      const loaded = allPages.reduce((count, p) => count + p.items.length, 0);
      return loaded < lastPage.totalCount ? loaded : undefined;
    },
    enabled,
    // Same cadence as the summary above, so the header figures and the table
    // below them always describe the same moment. Only the open tab's query is
    // enabled, so at most one list polls at a time.
    staleTime: REPORT_STALE_TIME_MS,
    refetchInterval: REPORT_REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  return {
    items: query.data?.pages.flatMap((p) => p.items) ?? [],
    // totalCount describes the whole filtered set, so the first page is
    // authoritative for it.
    totalCount: query.data?.pages[0]?.totalCount ?? 0,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
    refetch: query.refetch,
  };
}

export interface OffScreensListInfiniteFilters {
  columnFilters?: OffScreenColumnFilter[] | null;
  // Part of the query key, so changing it restarts the infinite list rather
  // than appending a differently-scoped page to the rows already loaded.
  minOffDays?: number | null;
}

export interface NoScheduleListInfiniteFilters {
  columnFilters?: NoScheduleColumnFilter[] | null;
}

export const useOffScreensListInfinite = (
  filters: OffScreensListInfiniteFilters = {},
  enabled: boolean = true
) =>
  useInfiniteReportList<OffScreenInfo>(
    ["offScreensList", "infinite", filters],
    (limit, offset) =>
      reportService.getOffScreensList({ ...filters, limit, offset }),
    { enabled }
  );

export const useScreensWithoutSchedulesListInfinite = (
  filters: NoScheduleListInfiniteFilters = {},
  enabled: boolean = true
) =>
  useInfiniteReportList<ScreenWithoutScheduleInfo>(
    ["screensWithoutSchedulesList", "infinite", filters],
    (limit, offset) =>
      reportService.getScreensWithoutSchedulesList({
        ...filters,
        limit,
        offset,
      }),
    { enabled }
  );

export interface CreativeReportScreensInfiniteFilters {
  startDate?: string | null;
  endDate?: string | null;
  cityId?: string | null;
  regionId?: string | null;
  search?: string | null;
}

export const useCreativeReportScreensInfinite = (
  creativeId: string | null,
  filters: CreativeReportScreensInfiniteFilters = {},
  enabled: boolean = true
) =>
  useInfiniteReportList<CreativeScreenPlayInfo>(
    ["creativeReportScreens", "infinite", creativeId, filters],
    (limit, offset) =>
      reportService.getCreativeReportScreens(creativeId as string, {
        ...filters,
        limit,
        offset,
      }),
    { enabled: enabled && !!creativeId }
  );

export const useActiveScreensListInfinite = (
  date?: string | null,
  search?: string | null,
  enabled: boolean = true
) =>
  useInfiniteReportList<ActiveScreenInfo>(
    ["activeScreensList", "infinite", date, search],
    (limit, offset) =>
      reportService.getActiveScreensList({ date, search, limit, offset }),
    { enabled }
  );

export type ScreenOffHistoryInfiniteFilters = Omit<
  ScreenOffHistoryFilters,
  "limit" | "offset"
> & {
  pageSize?: number;
};

export const useScreenOffHistoryInfinite = (
  filters: ScreenOffHistoryInfiniteFilters = {},
  enabled: boolean = true
) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;

  const query = useInfiniteQuery<ScreenOffHistoryData, Error>({
    queryKey: ["screenOffHistory", "infinite", { ...rest, pageSize }],
    // This endpoint is limit/offset based, so the page param is the offset itself.
    queryFn: ({ pageParam }) =>
      reportService.getScreenOffHistory({
        ...rest,
        limit: pageSize,
        offset: pageParam as number,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      // Stop on an empty page so a totalCount/entries mismatch can't loop forever.
      if (lastPage.entries.length === 0) return undefined;
      const loaded = allPages.reduce((count, p) => count + p.entries.length, 0);
      return loaded < lastPage.totalCount ? loaded : undefined;
    },
    enabled,
    // Kept in step with the summary and the other report tables — see
    // REPORT_REFRESH_INTERVAL_MS.
    staleTime: REPORT_STALE_TIME_MS,
    refetchInterval: REPORT_REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  const firstPage = query.data?.pages[0];

  return {
    entries: query.data?.pages.flatMap((p) => p.entries) ?? [],
    // The summary figures describe the whole filtered range, not the loaded
    // slice, so the first page is authoritative for all of them.
    totalCount: firstPage?.totalCount ?? 0,
    ongoingCount: firstPage?.ongoingCount ?? 0,
    totalOffMinutes: firstPage?.totalOffMinutes ?? 0,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
    refetch: query.refetch,
  };
};

export const useScreenTimeline = (
  screenId: string | null | undefined,
  filters: ScreenTimelineFilters = {},
  enabled: boolean = true
) => {
  const { startDate, endDate } = filters;

  const {
    data: timeline,
    isLoading,
    error,
    refetch,
  } = useQuery<ScreenTimelineData, Error>({
    queryKey: ["screenStatusTimeline", screenId, { startDate, endDate }],
    queryFn: () =>
      reportService.getScreenStatusTimeline(screenId as string, {
        startDate,
        endDate,
      }),
    enabled: enabled && !!screenId,
  });

  return {
    timeline,
    isLoading,
    error,
    refetch,
  };
};

// Header figures for the Creative Report. The played-screens rows come from
// useCreativeReportScreensInfinite; the full list (needed by the editable
// download flow) is fetched on demand via reportService.getCreativeReport.
export const useCreativeReport = (
  creativeId: string | null,
  filters?: {
    startDate?: string | null;
    endDate?: string | null;
    cityId?: string | null;
    regionId?: string | null;
  }
) => {
  const {
    data: reportData,
    isLoading,
    error,
    refetch,
  } = useQuery<CreativeReportSummary, Error>({
    queryKey: ["creativeReportSummary", creativeId, filters],
    queryFn: () => {
      if (!creativeId) {
        throw new Error("Creative ID is required");
      }
      return reportService.getCreativeReportSummary(creativeId, filters);
    },
    enabled: !!creativeId,
  });

  return {
    reportData,
    isLoading,
    error,
    refetch,
  };
};

export const useActiveScreenReport = (date?: string | null) => {
  const {
    data: reportData,
    isLoading,
    error,
    refetch,
  } = useQuery<ActiveScreenReportData, Error>({
    queryKey: ["activeScreenReport", date],
    queryFn: () => reportService.getActiveScreenReport(date),
  });

  return {
    reportData,
    isLoading,
    error,
    refetch,
  };
};
